import { z } from 'zod';

/**
 * Browser-visible configuration. Validated at module load so a bad deploy fails
 * on the first paint with a readable message instead of a 404 storm later.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default('/api/v1'),
  VITE_ENABLE_MSW: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  /**
   * Business accounts (business spec, working agreement).
   *
   * A compile-time constant, unlike the API's runtime flag: Vite inlines it at
   * build time, so turning it on means a rebuild and a deploy rather than an
   * environment change. Worth knowing before planning a flag flip.
   */
  VITE_BUSINESS_ACCOUNTS_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
});

export type WebEnv = z.infer<typeof envSchema>;

function parseEnv(): WebEnv {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
      .join('; ');
    throw new Error(`Invalid web environment configuration — ${issues}`);
  }

  return result.data;
}

export const env: WebEnv = parseEnv();
